import React from 'react';

interface RichTextChild {
  text?: string;
  type?: string;
  url?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
  children?: RichTextChild[];
}

interface RichTextBlock {
  type: string;
  level?: number;
  format?: 'ordered' | 'unordered';
  children?: RichTextChild[];
}

interface RichTextRendererProps {
  content: any;
  className?: string;
}

const renderTextNode = (child: RichTextChild, key: string | number): React.ReactNode => {
  if (child.type === 'link') {
    return (
      <a
        key={key}
        href={child.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-purple-600 hover:text-brand-purple-800 underline transition-colors"
      >
        {child.children?.map((subChild, subIdx) => renderTextNode(subChild, subIdx))}
      </a>
    );
  }

  let node: React.ReactNode = child.text ?? '';

  if (child.code) {
    node = <code key={`${key}-code`} className="bg-gray-100 text-brand-purple-900 px-1.5 py-0.5 rounded text-sm font-mono">{node}</code>;
  }
  if (child.strikethrough) {
    node = <del key={`${key}-del`}>{node}</del>;
  }
  if (child.underline) {
    node = <u key={`${key}-u`}>{node}</u>;
  }
  if (child.italic) {
    node = <em key={`${key}-em`}>{node}</em>;
  }
  if (child.bold) {
    node = <strong key={`${key}-bold`} className="font-bold text-gray-900">{node}</strong>;
  }

  return <React.Fragment key={key}>{node}</React.Fragment>;
};

const renderBlock = (block: RichTextBlock, idx: number): React.ReactNode => {
  switch (block.type) {
    case 'paragraph': {
      return (
        <p key={idx} className="mb-6">
          {block.children?.map((child, cIdx) => renderTextNode(child, cIdx))}
        </p>
      );
    }

    case 'heading': {
      const level = Math.min(Math.max(block.level || 2, 1), 6);
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;
      const sizeClasses: Record<number, string> = {
        1: 'text-3xl md:text-4xl mt-10 mb-6',
        2: 'text-2xl md:text-3xl mt-8 mb-4',
        3: 'text-xl md:text-2xl mt-6 mb-3',
        4: 'text-lg md:text-xl mt-5 mb-2',
        5: 'text-base md:text-lg mt-4 mb-2',
        6: 'text-sm md:text-base mt-4 mb-2',
      };
      return (
        <Tag key={idx} className={`font-serif font-bold text-brand-purple-900 ${sizeClasses[level]}`}>
          {block.children?.map((child, cIdx) => renderTextNode(child, cIdx))}
        </Tag>
      );
    }

    case 'list': {
      const isOrdered = block.format === 'ordered';
      const ListTag = isOrdered ? 'ol' : 'ul';
      const listClass = isOrdered ? 'list-decimal' : 'list-disc';
      return (
        <ListTag key={idx} className={`${listClass} list-inside my-6 space-y-2 pl-4 text-gray-700`}>
          {block.children?.map((item: any, i: number) => (
            <li key={i}>
              {item.children?.map((child: any, cIdx: number) => renderTextNode(child, cIdx))}
            </li>
          ))}
        </ListTag>
      );
    }

    case 'quote': {
      return (
        <blockquote key={idx} className="border-l-4 border-brand-purple-500 pl-4 py-2 my-6 italic text-gray-700 bg-brand-neutral-bg/40 rounded-r-lg">
          {block.children?.map((child, cIdx) => renderTextNode(child, cIdx))}
        </blockquote>
      );
    }

    default:
      return null;
  }
};

export const RichTextRenderer: React.FC<RichTextRendererProps> = ({ content, className = '' }) => {
  if (!content) return null;

  if (Array.isArray(content)) {
    return (
      <div className={className}>
        {content.map((block: RichTextBlock, idx: number) => renderBlock(block, idx))}
      </div>
    );
  }

  if (typeof content === 'string') {
    return (
      <div
        className={className}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  return null;
};

export default RichTextRenderer;
